import classNames from "classnames";
import QuickAddTask from "@/features/global/components/QuickAddTask";
import useEffectiveDisplayUser from "@/hooks/useEffectiveDisplayUser";
import RecentTasksTable from "./RecentTasksTable";
import RecentInteractionsTable from "./RecentInteractionsTable";
import RecentlyVisitedTable from "./RecentlyVisitedTable";
import DashboardEventsPanels from "./DashboardEventsPanels";
import useUpcomingTasks from "../_hooks/useUpcomingTasks";
import usePendingChangeRequests from "../_hooks/usePendingChangeRequests";
import useIsDashboardSingleColumn from "../_hooks/useIsDashboardSingleColumn";
import useCapabilities, {
  useCanManageTasksBool,
  useCanViewInteractionsBool,
  PERMISSION
} from "@/hooks/useCapabilities";
import { DASHBOARD_BOTTOM_SCROLL_PADDING_CLASS } from "../_utils/dashboardPanelStyles";
import {
  getDashboardColumnsLayoutClassName,
  getDashboardPrimaryColumnClassName,
  getDashboardSidebarColumnClassName
} from "../_utils/dashboardLayoutClasses";
import {
  shouldShowChangeRequestsPanel,
  shouldShowChangeRequestsSection
} from "../_utils/changeRequestsPanelVisibility";
import { shouldShowTodoPanel } from "../_utils/todoPanelVisibility";
import { useShowRosterDashboardTab } from "@/features/roster-activity";

export default function HomeDashboardBody() {
  const isSingleColumnLayout = useIsDashboardSingleColumn();
  const showRosterDashboardTab = useShowRosterDashboardTab();
  const { tasks, loading: tasksLoading, error: tasksError, totalOpenTaskCount, canViewTasks } = useUpcomingTasks();
  const {
    changeRequests,
    totalPendingCount,
    canManageChangeRequests,
    permissionProbeLoading,
    loading: changeRequestsLoading,
    sectionId: changeRequestsSectionId
  } = usePendingChangeRequests();
  const canViewInteractions = useCanViewInteractionsBool();
  const canManageTasks = useCanManageTasksBool();
  const { hasPermission, loaded: capabilitiesLoaded } = useCapabilities();
  const canViewEvents = capabilitiesLoaded && hasPermission(PERMISSION.manageEvents);
  const showChangeRequestsPanel = shouldShowChangeRequestsPanel({
    sectionId: changeRequestsSectionId,
    canManageChangeRequests,
    permissionProbeLoading,
    canViewTasks
  });
  const showChangeRequestsSection = shouldShowChangeRequestsSection({
    sectionId: changeRequestsSectionId,
    canManageChangeRequests,
    permissionProbeLoading,
    canViewTasks,
    totalPendingCount
  });
  const showTodoPanel = shouldShowTodoPanel({
    canViewTasks,
    canManageTasks,
    tasksLoading,
    tasksError: Boolean(tasksError),
    taskCount: tasks.length,
    totalOpenTaskCount,
    showChangeRequestsSection
  });
  const hasPrimaryColumn = showTodoPanel || canViewEvents || canManageTasks;
  const user = useEffectiveDisplayUser();
  const teamName = user.department;

  return (
    <div
      className={classNames(
        getDashboardColumnsLayoutClassName(isSingleColumnLayout, showRosterDashboardTab),
        DASHBOARD_BOTTOM_SCROLL_PADDING_CLASS
      )}
    >
      {hasPrimaryColumn ? (
        <div className={getDashboardPrimaryColumnClassName(isSingleColumnLayout)}>
          {canManageTasks ? <QuickAddTask /> : null}
          {showTodoPanel ? (
            <RecentTasksTable
              tasks={tasks}
              loading={tasksLoading}
              error={tasksError}
              totalOpenTaskCount={totalOpenTaskCount}
              teamName={teamName}
              canViewTasks={canViewTasks}
              changeRequests={changeRequests}
              changeRequestsLoading={changeRequestsLoading}
              totalPendingChangeRequestCount={totalPendingCount}
              showChangeRequestsSection={showChangeRequestsSection}
              changeRequestsSectionId={showChangeRequestsPanel ? changeRequestsSectionId : null}
            />
          ) : null}
          {canViewEvents ? <DashboardEventsPanels /> : null}
        </div>
      ) : null}
      <div className={getDashboardSidebarColumnClassName(isSingleColumnLayout, hasPrimaryColumn)}>
        <RecentlyVisitedTable />
        {canViewInteractions ? <RecentInteractionsTable /> : null}
      </div>
    </div>
  );
}
